import InputSlider from '@/components/sections/builder/settings/input-slider';
import InputTab from '@/components/sections/builder/settings/input-tab';
import SchemeSetting from '@/components/sections/builder/settings/scheme-setting';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';

import { ThemeProductCardType, useBuilderStore } from '@/stores/builder';
import { shallow } from 'zustand/shallow';

export const DEFAULT_THEME_PRODUCT_CARD = {
  style: 'standard',
  imagePadding: 0,
  textAlignment: 'left',
  borderThickness: 0,
  borderOpacity: 0,
  borderCornerRadius: 0,
  shadowOpacity: 0,
  shadowHorizontalOffset: 0,
  sahdowVerticalOffset: 0,
  shadowBlur: 0,
  limitCharacters: 50,
  variantPicker: 'dropdown',
  showButtonOnHover: false,
  buttonTransparent: false,
  buttonPosition: 'inside',
  buttonText: 'Choose Options',
  fontSizeTitle: 100,
  fontSizePrice: 100,
  positionButtonAddToCart: 'relative',
  qtySoldLabel: 'Sold',
  showQtySold: false,
};

export const DEFAULT_THEME_PRODUCT_DETAIL_BUTTON = {
  showButtonBuyNow: true,
  reverseButton: false,
  buttonLabelAddToCart: 'Add to cart',
  buttonLabelBuyNow: 'Buy now',
};

export default function ThemeProductCardSetting() {
  const builder = useBuilderStore(
    (state) => ({
      theme: state.theme,
      setTheme: state.setTheme,
    }),
    shallow
  );

  const productCard: ThemeProductCardType =
    builder.theme?.productCard || DEFAULT_THEME_PRODUCT_CARD;

  const setValue = (newValue: Partial<ThemeProductCardType>) => {
    builder.setTheme({
      productCard: { ...productCard, ...newValue },
    });
  };

  return (
    <div>
      <h3 className='mb-2 mt-4 font-bold'>Style</h3>
      <InputTab
        grid={2}
        defaultValue={productCard.style}
        options={[
          { label: 'Standard', value: 'standard' },
          { label: 'Card', value: 'card' },
        ]}
        onChange={(val) => setValue({ style: val })}
      />

      <InputSlider
        label='Image Padding'
        value={productCard.imagePadding}
        max={24}
        min={0}
        onChange={(val) => setValue({ imagePadding: val })}
        rightContent={<div>px</div>}
      />
      <div className='mb-1 mt-4'>Text Alignment</div>
      <InputTab
        defaultValue={productCard.textAlignment}
        options={[
          { label: 'Left', value: 'left' },
          { label: 'Center', value: 'center' },
          { label: 'Right', value: 'right' },
        ]}
        onChange={(val) => setValue({ textAlignment: val })}
      />
      <InputSlider
        label='Font size title scale'
        value={productCard.fontSizeTitle || 100}
        max={200}
        min={100}
        step={10}
        onChange={(val) =>
          setValue({
            fontSizeTitle: val,
          })
        }
      />
      <InputSlider
        label='Font size price scale'
        value={productCard.fontSizePrice || 100}
        max={200}
        min={100}
        step={10}
        onChange={(val) =>
          setValue({
            fontSizePrice: val,
          })
        }
      />
      <div className='mb-1 mt-4'>Button position</div>
      <InputTab
        defaultValue={productCard.buttonPosition || DEFAULT_THEME_PRODUCT_CARD.buttonPosition}
        grid={2}
        options={[
          { label: 'Outside', value: 'outside' },
          { label: 'Inside', value: 'inside' },
        ]}
        onChange={(val) => setValue({ buttonPosition: val })}
      />
      <div className='mb-1 mt-4'>Button text</div>
      <Input
        name='buttonText'
        defaultValue={productCard.buttonText || DEFAULT_THEME_PRODUCT_CARD.buttonText}
        value={productCard.buttonText}
        maxLength={30}
        onChange={(e) => {
          setValue({
            buttonText: e.target.value,
          });
        }}
      />
      <div className='mb-1 mt-4'>Sold info text</div>
      <Input
        name='qtySoldLabel'
        defaultValue={productCard.qtySoldLabel || DEFAULT_THEME_PRODUCT_CARD.qtySoldLabel}
        value={productCard.qtySoldLabel}
        maxLength={30}
        onChange={(e) => {
          setValue({
            qtySoldLabel: e.target.value,
          });
        }}
      />
      <div className='mt-4 flex gap-x-1'>
        <Checkbox
          id='showButtonOnHover'
          className='mr-2'
          checked={productCard.showButtonOnHover}
          onCheckedChange={(checked) => {
            setValue({ showButtonOnHover: checked as boolean });
          }}
        />
        <label htmlFor='showButtonOnHover'>Show button on hover</label>
      </div>
      <div className='mt-4 flex gap-x-1'>
        <Checkbox
          id='buttonTransparent'
          className='mr-2'
          checked={productCard.buttonTransparent}
          onCheckedChange={(checked) => {
            setValue({ buttonTransparent: checked as boolean });
          }}
        />
        <label htmlFor='buttonTransparent'>Button transparent</label>
      </div>
      <div className='mt-4 flex gap-x-1'>
        <Checkbox
          id='showQtySold'
          className='mr-2'
          checked={productCard.showQtySold}
          onCheckedChange={(checked) => {
            setValue({ showQtySold: checked as boolean });
          }}
        />
        <label htmlFor='showQtySold'>Show quantity sold</label>
      </div>
      <div className='mb-2 mt-4'>Color scheme</div>
      <SchemeSetting
        value={productCard.colorScheme}
        onChange={(theme) => setValue({ colorScheme: theme })}
      />
      <div className='mt-1 text-xs'>
        To edit all your theme&apos;s colors, go to your color theme settings.
      </div>
      <h3 className='mb-2 mt-4 font-bold'>Border</h3>
      <InputSlider
        label='Thickness'
        value={productCard.borderThickness}
        max={12}
        min={0}
        onChange={(val) => setValue({ borderThickness: val })}
        rightContent={<div>px</div>}
      />
      <InputSlider
        label='Opacity'
        value={productCard.borderOpacity}
        max={100}
        min={0}
        onChange={(val) => setValue({ borderOpacity: val })}
        rightContent={<div>%</div>}
      />
      <InputSlider
        label='Corner Radius'
        value={productCard.borderCornerRadius}
        max={40}
        min={0}
        onChange={(val) => setValue({ borderCornerRadius: val })}
        rightContent={<div>px</div>}
      />
      <h3 className='mb-2 mt-4 font-bold'>Shadow</h3>
      <InputSlider
        label='Opacity'
        value={productCard.shadowOpacity}
        max={100}
        min={0}
        onChange={(val) => setValue({ shadowOpacity: val })}
        rightContent={<div>%</div>}
      />
      <InputSlider
        label='Horizontal Offset'
        value={productCard.shadowHorizontalOffset}
        max={12}
        min={-12}
        onChange={(val) => setValue({ shadowHorizontalOffset: val })}
        rightContent={<div>px</div>}
      />
      <InputSlider
        label='Vertical Offset'
        value={productCard.sahdowVerticalOffset}
        max={12}
        min={-12}
        onChange={(val) => setValue({ sahdowVerticalOffset: val })}
        rightContent={<div>px</div>}
      />
      <InputSlider
        label='Shadow Blur'
        value={productCard.shadowBlur}
        max={20}
        min={0}
        onChange={(val) => setValue({ shadowBlur: val })}
        rightContent={<div>px</div>}
      />
      <InputSlider
        label='Limit Charecters'
        value={productCard.limitCharacters || 50}
        max={200}
        min={50}
        onChange={(val) => setValue({ limitCharacters: val })}
      />
    </div>
  );
}
